import { N, round, ceil } from './helpers.mjs';

export const BRICKLAYERS = [
  // ====================== BRICKLAYERS ======================
  {
    slug: 'bricks-per-area',
    trade: 'bricklayers',
    name: 'Bricks Required',
    blurb: 'Standard UK brick (215×102.5×65mm) — 60 bricks/m² incl. 10mm mortar.',
    inputs: [
      { key: 'length', label: 'Wall length', unit: 'm', placeholder: '10' },
      { key: 'height', label: 'Wall height', unit: 'm', placeholder: '2.4' },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
      { key: 'skin', label: 'Skins (1 = single, 2 = cavity)', unit: '', placeholder: '1', default: 1 },
    ],
    compute: (v) => {
      const area = N(v.length) * N(v.height);
      const base = area * 60 * (N(v.skin) || 1);
      const total = ceil(base * (1 + N(v.wastage) / 100));
      return [
        { label: 'Wall area', value: round(area), unit: 'm²' },
        { label: 'Bricks (incl. wastage)', value: total, unit: 'bricks' },
        { label: 'Packs of 500', value: ceil(total / 500), unit: 'packs' },
      ];
    },
  },
  {
    slug: 'blocks-per-area',
    trade: 'bricklayers',
    name: 'Concrete Blocks Required',
    blurb: 'Solid, hollow, aircrete and fairface blocks — all common UK sizes (440×215, 390×190 etc.) at 10mm bed/perp joints.',
    inputs: [
      { key: 'length', label: 'Wall length', unit: 'm', placeholder: '10' },
      { key: 'height', label: 'Wall height', unit: 'm', placeholder: '2.4' },
      {
        key: 'blockType',
        label: 'Block type',
        type: 'select',
        default: 'std-100',
        options: [
          // Standard 440 × 215 face (UK std), thickness varies
          { value: 'std-100',         label: 'Standard solid 440×215×100mm (10/m²)' },
          { value: 'std-140',         label: 'Standard solid 440×215×140mm (10/m²)' },
          { value: 'std-215',         label: 'Standard solid 440×215×215mm (10/m²)' },
          // Hollow
          { value: 'hollow-100',      label: 'Hollow concrete 440×215×100mm (10/m²)' },
          { value: 'hollow-140',      label: 'Hollow concrete 440×215×140mm (10/m²)' },
          { value: 'hollow-215',      label: 'Hollow concrete 440×215×215mm (10/m²)' },
          // Aircrete (Thermalite / Celcon)
          { value: 'aircrete-100',    label: 'Aircrete / Thermalite 440×215×100mm (10/m²)' },
          { value: 'aircrete-140',    label: 'Aircrete / Thermalite 440×215×140mm (10/m²)' },
          // Dense / medium dense face
          { value: 'mediumdense-100', label: 'Medium-dense 440×215×100mm (10/m²)' },
          { value: 'dense-100',       label: 'Dense concrete 440×215×100mm (10/m²)' },
          // 390 × 190 face (architectural / fairface)
          { value: 'fairface-100',    label: 'Fairface 390×190×100mm (13.2/m²)' },
          { value: 'fairface-140',    label: 'Fairface 390×190×140mm (13.2/m²)' },
          { value: 'fairface-190',    label: 'Fairface 390×190×190mm (13.2/m²)' },
          // Coursing brick (used for half-block heights / levelling)
          { value: 'coursing-brick',  label: 'Coursing brick 215×65×100mm (33/m²)' },
          // Custom
          { value: 'custom',          label: 'Custom block size (enter face dims below)' },
        ],
      },
      { key: 'customL', label: 'Custom face length (Custom only)', unit: 'mm', placeholder: '440', default: 440 },
      { key: 'customH', label: 'Custom face height (Custom only)', unit: 'mm', placeholder: '215', default: 215 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      // Blocks per m² (face area inc. 10mm bed/perp joints)
      const PER_M2 = {
        'std-100': 10, 'std-140': 10, 'std-215': 10,
        'hollow-100': 10, 'hollow-140': 10, 'hollow-215': 10,
        'aircrete-100': 10, 'aircrete-140': 10,
        'mediumdense-100': 10, 'dense-100': 10,
        'fairface-100': 13.2, 'fairface-140': 13.2, 'fairface-190': 13.2,
        'coursing-brick': 33,
      };
      const blockType = v.blockType || 'std-100';
      let perM2 = PER_M2[blockType] || 10;
      let typeLabel = 'Blocks required';
      if (blockType === 'custom') {
        const cL = N(v.customL); const cH = N(v.customH);
        if (cL > 0 && cH > 0) perM2 = 1_000_000 / ((cL + 10) * (cH + 10));
        typeLabel = `Blocks required (${Math.round(N(v.customL) || 0)}×${Math.round(N(v.customH) || 0)}mm face)`;
      }
      const area = N(v.length) * N(v.height);
      const total = ceil(area * perM2 * (1 + N(v.wastage) / 100));
      return [
        { label: 'Wall area', value: round(area, 2), unit: 'm²' },
        { label: 'Blocks per m²', value: round(perM2, 1), unit: 'blocks/m²' },
        { label: typeLabel, value: total, unit: 'blocks' },
      ];
    },
  },
  {
    slug: 'mortar-mix',
    trade: 'bricklayers',
    name: 'Mortar Quantity & Mix',
    blurb: 'Brickwork or blockwork, any mix ratio (1:3 to 1:8). Calculates mortar volume, cement bags, sand tonnage and bulk bags.',
    inputs: [
      {
        key: 'workType',
        label: 'Type of work',
        type: 'select',
        default: 'bricks',
        options: [
          { value: 'bricks',       label: 'Brickwork (0.85 m³ per 1000 bricks)' },
          { value: 'blocks-100',   label: 'Blockwork 100mm (0.5 m³ per 100 blocks)' },
          { value: 'blocks-140',   label: 'Blockwork 140mm (0.6 m³ per 100 blocks)' },
          { value: 'blocks-215',   label: 'Blockwork 215mm (0.7 m³ per 100 blocks)' },
          { value: 'blocks-fairface', label: 'Fairface 390×190 (0.4 m³ per 100 blocks)' },
        ],
      },
      { key: 'quantity', label: 'Quantity (bricks or blocks)', unit: '', placeholder: '1000' },
      {
        key: 'mix',
        label: 'Mix ratio (cement : sand)',
        type: 'select',
        default: '1:5',
        options: [
          { value: '1:3', label: '1:3 — strong / structural (engineering bricks, exposed)' },
          { value: '1:4', label: '1:4 — general purpose (most brickwork)' },
          { value: '1:5', label: '1:5 — general purpose blockwork (most common)' },
          { value: '1:6', label: '1:6 — lean mix (interior / sheltered blockwork)' },
          { value: '1:8', label: '1:8 — soft / lime-modified (heritage work)' },
        ],
      },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => {
      // Mortar volume per unit
      const MORTAR_PER_UNIT = {
        'bricks':           0.00085, // per brick (0.85 m³ / 1000)
        'blocks-100':       0.005,   // per block (0.5 m³ / 100)
        'blocks-140':       0.006,
        'blocks-215':       0.007,
        'blocks-fairface':  0.004,
      };
      const work = v.workType || 'bricks';
      const perUnit = MORTAR_PER_UNIT[work] || 0.00085;
      const qty = N(v.quantity);
      const wastage = N(v.wastage) || 0;
      const m3 = qty * perUnit * (1 + wastage / 100);
      // Mix ratio: "1:X" cement:sand by volume. Wet→dry compaction factor 1.25.
      const mixRaw = (v.mix || '1:5').split(':');
      const X = Math.max(1, parseFloat(mixRaw[1]) || 5);
      const dry = m3 * 1.25;
      const cementVolM3 = dry / (1 + X);
      const sandVolM3 = dry * X / (1 + X);
      const cementKg = cementVolM3 * 1440;  // cement bulk density ~1440 kg/m³
      const sandKg = sandVolM3 * 1600;       // building sand ~1600 kg/m³
      const unitLabel = work === 'bricks' ? 'bricks' : 'blocks';
      return [
        { label: 'Mortar volume', value: round(m3, 3), unit: 'm³' },
        { label: `Mix used (${qty.toLocaleString()} ${unitLabel})`, value: `1 : ${X} cement:sand`, unit: '' },
        { label: 'Cement (25kg bags)', value: ceil(cementKg / 25), unit: 'bags' },
        { label: 'Building sand', value: round(sandKg / 1000, 2), unit: 'tonnes' },
        { label: 'Sand bulk bags (0.85t each)', value: ceil((sandKg / 1000) / 0.85), unit: 'bags' },
      ];
    },
  },

  {
    slug: 'wall-ties',
    trade: 'bricklayers',
    name: 'Wall Tie Quantity & Spacing',
    blurb: 'Cavity wall ties to PD 6697 / NHBC — standard 2.5/m² (900mm horizontal × 450mm vertical), plus extra ties around openings at 300mm vertical centres.',
    inputs: [
      { key: 'length', label: 'Wall length', unit: 'm', placeholder: '10' },
      { key: 'height', label: 'Wall height', unit: 'm', placeholder: '2.4' },
      {
        key: 'density',
        label: 'Tie density',
        type: 'select',
        default: '2.5',
        options: [
          { value: '2.5', label: 'Standard 2.5/m² — 900 × 450mm (cavity ≤ 100mm)' },
          { value: '4.4', label: 'Exposed / wide cavity 4.4/m² — 750 × 300mm' },
        ],
      },
      { key: 'openings', label: 'Number of openings (doors/windows)', unit: '', placeholder: '4', default: 0 },
      { key: 'revealHeight', label: 'Avg reveal height per opening', unit: 'm', placeholder: '1.2', default: 1.2 },
      { key: 'wastage', label: 'Wastage', unit: '%', placeholder: '5', default: 5 },
    ],
    compute: (v) => {
      const area = N(v.length) * N(v.height);
      const density = parseFloat(v.density) || 2.5;
      const fieldTies = area * density;
      // Extra ties at openings: each opening has 2 reveals, ties at 300mm vertical centres
      const extra = N(v.openings) * 2 * (N(v.revealHeight) > 0 ? Math.ceil(N(v.revealHeight) / 0.3) : 0);
      const total = Math.ceil((fieldTies + extra) * (1 + N(v.wastage) / 100));
      return [
        { label: 'Wall area', value: round(area, 2), unit: 'm²' },
        { label: `Field ties (${density}/m²)`, value: Math.ceil(fieldTies), unit: 'ties' },
        { label: 'Extra ties at openings', value: extra, unit: 'ties' },
        { label: 'Total ties (incl. wastage)', value: total, unit: 'ties' },
        { label: 'Boxes of 250', value: ceil(total / 250), unit: 'boxes' },
      ];
    },
  },
  {
    slug: 'dpc',
    trade: 'bricklayers',
    name: 'DPC (Damp Proof Course)',
    blurb: 'Linear metres of damp proof course and rolls needed. A cavity wall needs a run in each leaf.',
    inputs: [
      { key: 'length', label: 'Total wall length', unit: 'm', placeholder: '30' },
      {
        key: 'leaves',
        label: 'Wall type',
        type: 'select',
        default: '2',
        options: [
          { value: '1', label: 'Single leaf (1 run)' },
          { value: '2', label: 'Cavity wall (2 runs — inner + outer)' },
        ],
      },
      {
        key: 'roll',
        label: 'Roll length',
        type: 'select',
        default: '30',
        options: [
          { value: '30', label: '30m roll (standard)' },
          { value: '20', label: '20m roll' },
          { value: '8', label: '8m roll' },
        ],
      },
      { key: 'wastage', label: 'Wastage / laps', unit: '%', placeholder: '10', default: 10 },
    ],
    compute: (v) => {
      const runs = parseInt(v.leaves) || 1;
      const lm = N(v.length) * runs * (1 + N(v.wastage) / 100);
      const roll = parseFloat(v.roll) || 30;
      return [
        { label: 'DPC required', value: round(lm, 1), unit: 'm' },
        { label: `Rolls (${roll}m each)`, value: ceil(lm / roll), unit: 'rolls' },
      ];
    },
  },

];
